import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { newsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Newspaper, Plus, Pencil, Trash2, Loader2, Save, Eye } from 'lucide-react';

const NewsManager = () => {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ message: '', color: '#ff0000', bg_color: '#fff3cd', status: 'active' });

  useEffect(() => { fetchNews(); }, []);

  const fetchNews = async () => {
    try { const r = await newsAPI.list(); setNewsList(r.data || []); } catch {} finally { setLoading(false); }
  };

  const resetForm = () => { setForm({ message: '', color: '#ff0000', bg_color: '#fff3cd', status: 'active' }); setEditId(null); };

  const openEdit = (n) => { setForm({ message: n.message, color: n.color, bg_color: n.bg_color, status: n.status }); setEditId(n.id); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.message) { toast.error('Message is required'); return; }
    setSaving(true);
    try {
      if (editId) { await newsAPI.update(editId, form); toast.success('News updated'); }
      else { await newsAPI.create(form); toast.success('News added'); }
      fetchNews(); setDialogOpen(false); resetForm();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this news?')) return;
    try { await newsAPI.delete(id); toast.success('News deleted'); fetchNews(); } catch { toast.error('Failed'); }
  };

  const handleToggle = async (n) => {
    try {
      await newsAPI.update(n.id, { status: n.status === 'active' ? 'inactive' : 'active' });
      fetchNews();
    } catch {}
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="news-manager-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Newspaper className="w-6 h-6" style={{ color: '#4680ff' }} />News / Marquee</h1>
            <p className="text-muted-foreground">Manage scrolling news and announcements</p>
          </div>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#4680ff] hover:bg-[#2563eb] text-white shadow-lg shadow-blue-500/20 rounded-lg h-10 px-5 text-sm font-semibold btn-press" data-testid="add-news-btn">
            <Plus className="w-4 h-4 mr-2" />Add News
          </Button>
        </div>

        {/* Preview */}
        {newsList.filter(n => n.status === 'active').length > 0 && (
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center gap-2 px-4 py-2 text-sm font-medium" style={{ background: newsList.find(n => n.status === 'active')?.bg_color || '#fff3cd' }}>
                <Eye className="w-4 h-4 flex-shrink-0" />
                <span className="font-bold text-xs">PREVIEW:</span>
                <div className="overflow-hidden flex-1">
                  <div className="whitespace-nowrap animate-marquee">
                    {newsList.filter(n => n.status === 'active').map((n, i) => (
                      <span key={i} style={{ color: n.color }} className="mx-8">{n.message}</span>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">{[1,2].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
            ) : newsList.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground"><Newspaper className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No news added yet</p></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Message</TableHead>
                    <TableHead>Color</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {newsList.map(n => (
                    <TableRow key={n.id}>
                      <TableCell>
                        <span style={{ color: n.color, background: n.bg_color }} className="px-2 py-1 rounded text-sm">{n.message.substring(0, 80)}{n.message.length > 80 ? '...' : ''}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded border" style={{ background: n.color }} />
                          <span className="text-xs font-mono">{n.color}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <button onClick={() => handleToggle(n)}>
                          <Badge className={n.status === 'active' ? 'bg-green-100 text-green-800 cursor-pointer' : 'bg-red-100 text-red-800 cursor-pointer'}>{n.status}</Badge>
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(n)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(n.id)}><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent data-testid="news-dialog">
            <DialogHeader><DialogTitle>{editId ? 'Edit News' : 'Add News'}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Message *</Label><textarea rows={3} placeholder="Type your news message..." value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="news-message-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Text Color</Label><div className="flex gap-2"><input type="color" value={form.color} onChange={e => setForm({...form, color: e.target.value})} className="w-10 h-10 rounded cursor-pointer" /><Input value={form.color} onChange={e => setForm({...form, color: e.target.value})} className="flex-1" /></div></div>
                <div className="space-y-2"><Label>Background Color</Label><div className="flex gap-2"><input type="color" value={form.bg_color} onChange={e => setForm({...form, bg_color: e.target.value})} className="w-10 h-10 rounded cursor-pointer" /><Input value={form.bg_color} onChange={e => setForm({...form, bg_color: e.target.value})} className="flex-1" /></div></div>
              </div>
              {form.message && (
                <div className="space-y-2"><Label>Preview</Label><div className="p-3 rounded-lg text-sm" style={{ color: form.color, background: form.bg_color }}>{form.message}</div></div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#4680ff] hover:bg-[#2563eb] text-white shadow-lg shadow-blue-500/20 rounded-lg h-10 px-5 text-sm font-semibold btn-press" data-testid="save-news-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{editId ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <style>{`
        @keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee { animation: marquee 15s linear infinite; }
      `}</style>
    </DashboardLayout>
  );
};

export default NewsManager;
